import { useMemo, useState } from 'react'
import { useSkin } from '../lib/skin'

/* ============================================================================
   Route map.

   The tour drawn on one map: venues as pins, drives as a solid line, flights
   as a dashed arc. Clicking a leg opens it.

   Same tile composition as `Map`, but it has to frame a set of points rather
   than centre on one, so it computes the zoom that fits them all. Everything
   else — Carto tiles, skin-aware, no library, no key — is the same.

   Flights are drawn as arcs rather than straight lines for one honest reason:
   a straight line between two airports reads as a road, and on a map with
   roads on it that matters. The curve says "this bit is not driven".
   ============================================================================ */

const TILE = 256
const SUBDOMAINS = ['a', 'b', 'c', 'd']

export interface MapPoint {
  id: string
  lat: number
  lon: number
  label: string
}

export interface MapLeg {
  id: string
  from: MapPoint
  to: MapPoint
  mode: 'drive' | 'fly'
  label: string
  /** [lon, lat] pairs from the router. Absent until it resolves, and absent
   *  for flights, which have no road to follow. */
  line?: number[][]
}

function project(lat: number, lon: number, zoom: number) {
  const n = 2 ** zoom
  const radians = (Math.max(-85.05, Math.min(85.05, lat)) * Math.PI) / 180
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(radians) + 1 / Math.cos(radians)) / Math.PI) / 2) * n,
  }
}

export default function RouteMap({
  points,
  legs,
  height = 380,
  onSelectLeg,
  selectedLegId,
}: {
  points: MapPoint[]
  legs: MapLeg[]
  height?: number
  onSelectLeg?: (leg: MapLeg) => void
  selectedLegId?: string | null
}) {
  const { skin } = useSkin()
  const [width, setWidth] = useState(900)
  const retina = typeof window !== 'undefined' && window.devicePixelRatio > 1
  const style = skin === 'daylight' ? 'light_all' : 'dark_all'

  /**
   * The zoom that fits every point, with a margin so pins aren't on the edge.
   * Stepping down from the closest zoom until everything fits is exact and
   * costs nothing; solving it analytically is the same answer with more places
   * to get the Mercator wrong.
   */
  const view = useMemo(() => {
    if (points.length === 0) return null

    const lats = points.map((point) => point.lat)
    const lons = points.map((point) => point.lon)
    const centre = {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lon: (Math.min(...lons) + Math.max(...lons)) / 2,
    }

    const pad = 60
    let zoom = 12
    for (; zoom > 1; zoom--) {
      const projected = points.map((point) => project(point.lat, point.lon, zoom))
      const spanX = (Math.max(...projected.map((p) => p.x)) - Math.min(...projected.map((p) => p.x))) * TILE
      const spanY = (Math.max(...projected.map((p) => p.y)) - Math.min(...projected.map((p) => p.y))) * TILE
      if (spanX <= width - pad * 2 && spanY <= height - pad * 2) break
    }

    const middle = project(centre.lat, centre.lon, zoom)
    const originX = middle.x * TILE - width / 2
    const originY = middle.y * TILE - height / 2

    return {
      zoom,
      originX,
      originY,
      at: (lat: number, lon: number) => {
        const p = project(lat, lon, zoom)
        return { x: p.x * TILE - originX, y: p.y * TILE - originY }
      },
    }
  }, [points, width, height])

  const tiles = useMemo(() => {
    if (!view) return []
    const firstCol = Math.floor(view.originX / TILE)
    const firstRow = Math.floor(view.originY / TILE)
    const cols = Math.ceil(width / TILE) + 1
    const rows = Math.ceil(height / TILE) + 1
    const max = 2 ** view.zoom
    const out: Array<{ key: string; url: string; left: number; top: number }> = []

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const tileX = firstCol + col
        const tileY = firstRow + row
        if (tileY < 0 || tileY >= max) continue
        const wrapped = ((tileX % max) + max) % max
        out.push({
          key: `${view.zoom}/${tileX}/${tileY}`,
          url:
            `https://${SUBDOMAINS[(wrapped + tileY) % SUBDOMAINS.length]}.basemaps.cartocdn.com/` +
            `${style}/${view.zoom}/${wrapped}/${tileY}${retina ? '@2x' : ''}.png`,
          left: tileX * TILE - view.originX,
          top: tileY * TILE - view.originY,
        })
      }
    }
    return out
  }, [view, width, height, style, retina])

  if (points.length === 0 || !view) {
    return (
      <div
        className="panel"
        style={{ height, display: 'grid', placeItems: 'center' }}
      >
        <span className="t-caption">
          Nothing to draw yet. Shows with a saved location appear here.
        </span>
      </div>
    )
  }

  return (
    <div
      ref={(element) => {
        if (element && element.clientWidth && element.clientWidth !== width) {
          setWidth(element.clientWidth)
        }
      }}
      style={{
        position: 'relative',
        height,
        borderRadius: 'var(--radius-card)',
        overflow: 'hidden',
        border: 'var(--hairline-width) solid var(--hairline)',
        background: 'var(--panel-raised)',
      }}
    >
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          draggable={false}
          loading="lazy"
          style={{ position: 'absolute', left: tile.left, top: tile.top, width: TILE, height: TILE }}
        />
      ))}

      <svg
        width={width}
        height={height}
        style={{ position: 'absolute', inset: 0 }}
        aria-hidden
      >
        {legs.map((leg) => {
          const a = view.at(leg.from.lat, leg.from.lon)
          const b = view.at(leg.to.lat, leg.to.lon)
          const on = selectedLegId === leg.id
          const flying = leg.mode === 'fly'

          // A flight bows away from the straight line so it can't be mistaken
          // for a road on a map that has roads on it.
          const midX = (a.x + b.x) / 2
          const midY = (a.y + b.y) / 2
          const lift = flying ? Math.min(90, Math.hypot(b.x - a.x, b.y - a.y) * 0.22) : 0
          // A drive follows the actual road once the router has answered;
          // until then it is a straight line, which is honest about being a
          // placeholder rather than pretending to be a route.
          const road =
            !flying && leg.line && leg.line.length > 1 ?
              leg.line
                .map(([lon, lat], index) => {
                  const at = view.at(lat, lon)
                  return `${index === 0 ? 'M' : 'L'} ${at.x} ${at.y}`
                })
                .join(' ') :
              null

          const path = road
            ? road
            : flying
              ? `M ${a.x} ${a.y} Q ${midX} ${midY - lift} ${b.x} ${b.y}`
              : `M ${a.x} ${a.y} L ${b.x} ${b.y}`

          return (
            <g key={leg.id}>
              {/* A wide invisible stroke underneath, because a 3px line is not
                  a click target anybody can hit. */}
              <path
                d={path}
                stroke="transparent"
                strokeWidth={18}
                fill="none"
                style={{ cursor: onSelectLeg ? 'pointer' : 'default', pointerEvents: 'stroke' }}
                onClick={() => onSelectLeg?.(leg)}
              />
              <path
                d={path}
                stroke={on ? 'var(--accent)' : flying ? 'var(--text-tertiary)' : 'var(--accent-text)'}
                strokeWidth={on ? 4 : 2.5}
                strokeDasharray={flying ? '7 6' : undefined}
                strokeLinecap="round"
                fill="none"
                style={{ pointerEvents: 'none' }}
              />
            </g>
          )
        })}

        {points.map((point) => {
          const at = view.at(point.lat, point.lon)
          return (
            <g key={point.id} style={{ pointerEvents: 'none' }}>
              <circle cx={at.x} cy={at.y} r={7} fill="var(--accent)" stroke="#fff" strokeWidth={2.5} />
            </g>
          )
        })}
      </svg>

      {/* Labels sit outside the SVG so they inherit the app's type. */}
      {points.map((point) => {
        const at = view.at(point.lat, point.lon)
        return (
          <span
            key={point.id}
            className="t-spec-caption"
            style={{
              position: 'absolute',
              left: at.x + 11,
              top: at.y - 9,
              padding: '2px 6px',
              borderRadius: 5,
              background: 'var(--panel)',
              border: 'var(--hairline-width) solid var(--hairline)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
            }}
          >
            {point.label}
          </span>
        )
      })}

      <span
        className="t-spec-caption"
        style={{
          position: 'absolute',
          right: 8,
          bottom: 4,
          fontSize: '0.56rem',
          color: 'var(--text-tertiary)',
          pointerEvents: 'none',
        }}
      >
        © OpenStreetMap · © CARTO
      </span>
    </div>
  )
}
